import React, { useMemo } from "react";
import {
  CheckCircleIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Button, Card } from "@/shared";
import { useAuth, useConfig } from "@/hooks";
import { useUpdateAccountMutation } from "@/redux/services/account/accountApi";
import { getProductTerminology } from "@/constants/product-terminology";
import {
  getCoreStepsForUser,
  getNormalizedTutorialProgress,
  isAdminTutorialEligible,
} from "@/features/tutorials/tutorial.utils";

const OPEN_TUTORIAL_EVENT = "attune:tutorial-open";

/**
 * Post-signup welcome card for owners and admins on first dashboard visits.
 * Surfaces setup progress and launches the existing guided tour / checklist.
 */
export function GettingStartedWelcomeCard() {
  const { user } = useAuth();
  const { appType } = useConfig();
  const [updateAccount, { isLoading }] = useUpdateAccountMutation();

  const progress = useMemo(
    () => getNormalizedTutorialProgress(user?.tutorialProgress),
    [user?.tutorialProgress]
  );

  const eligible = isAdminTutorialEligible(user);
  const setupComplete = progress.coreAdminOnboardingCompleted;

  const terminology = getProductTerminology(appType);
  const steps = useMemo(() => {
    return getCoreStepsForUser(user, appType).map((step) => ({
      ...step,
      completed: Boolean(progress.coreSteps?.[step.id]),
    }));
  }, [user, appType, progress.coreSteps]);

  const completedCount = steps.filter((step) => step.completed).length;
  const companyName =
    user?.parentCompany?.name ||
    user?.company?.name ||
    user?.parentCompanyName ||
    null;

  if (!eligible || setupComplete) {
    return null;
  }

  const openTutorial = (type) => {
    window.dispatchEvent(
      new CustomEvent(OPEN_TUTORIAL_EVENT, {
        detail: { type },
      })
    );
  };

  const handleSkip = async () => {
    try {
      await updateAccount({
        tutorialProgress: {
          ...progress,
          coreAdminOnboardingCompleted: true,
          completedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      }).unwrap();
    } catch (error) {
      console.error("Failed to dismiss getting started card", error);
    }
  };

  return (
    <Card className="rounded-xl border-primary-200 bg-gradient-to-br from-primary-50 via-white to-white">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <SparklesIcon className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {companyName
                  ? `Welcome to AttuneLogic, ${companyName}`
                  : "Welcome to AttuneLogic"}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Your account is ready. Complete these steps to dispatch your first{" "}
                {terminology.jobLower}, invite your team, and start tracking
                operations.
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white/80 p-3">
            <div className="mb-2 flex items-center justify-between text-xs font-medium text-gray-500">
              <span>Setup progress</span>
              <span>
                {completedCount} of {steps.length} complete
              </span>
            </div>
            <ul className="space-y-2">
              {steps.map((step) => (
                <li key={step.id} className="flex items-start gap-2 text-sm">
                  <CheckCircleIcon
                    className={`mt-0.5 h-4 w-4 flex-shrink-0 ${
                      step.completed ? "text-green-600" : "text-gray-300"
                    }`}
                    aria-hidden="true"
                  />
                  <span
                    className={
                      step.completed ? "text-gray-500 line-through" : "text-gray-800"
                    }
                  >
                    {step.title}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex flex-shrink-0 flex-col gap-2 sm:flex-row lg:flex-col lg:items-stretch">
          <Button type="primary" onClick={() => openTutorial("full")}>
            Start guided tour
          </Button>
          <Button type="alternative" onClick={() => openTutorial("checklist")}>
            Open setup checklist
          </Button>
          <Button
            type="alternative"
            onClick={handleSkip}
            disabled={isLoading}
            className="text-gray-500"
          >
            <span className="inline-flex items-center gap-1">
              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
              Skip for now
            </span>
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default GettingStartedWelcomeCard;
